import * as braze from '@braze/web-sdk';
import { DI } from '@intigral/constant/config/DIContainer';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { container, UserInfo } from '@intigral/domain';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { IReminderService } from '@intigral/domain/lib/application/services/ReminderService';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { useQuery } from '@tanstack/react-query';
import h from '../../helpers/common';
import { excludeIdFromUrl } from '../../helpers/reminder';
import user from '../../helpers/user';
import { IReminderReducer, ReminderData } from '../../interfaces/StoreTypes';
import { initialState } from '../defaultState';
import { log } from '@intigral/utils';
import { isGuestUser } from '../../config/guestMode';

type GetReminderPropsTypes = {
    detailed: boolean;
    range?: string;
    isCount?: boolean;
    totalEntries?: number;
};

type SetReminderPropsTypes = {
    paId: string;
    listingId?: string;
    programTitle: string;
    index: number;
    type: string;
    reminderId?: string;
};

type UnSetReminderPropsTypes = {
    listingId: string;
    programTitle: string;
    index: number;
    reminderId: string;
};

interface reduxReminderData {
    remindersData: SetReminderPropsTypes[];
}

interface RootState {
    reminder: reduxReminderData;
}

export const useUserReminders = (payload: GetReminderPropsTypes, user: UserInfo, enabled = true) =>
    useQuery({
        queryKey: ['profiles', 'user-reminders'],
        /** Guest locate token is rejected by reminder APIs (same as profiles / device). */
        enabled: Boolean(enabled) && Boolean(user) && !isGuestUser(user),
        staleTime: 60 * 10 * 1000,
        queryFn: async () => {
            const reminderService = container.resolve<IReminderService>(DI.SERVICES.IReminderService);
            const response = await reminderService.listReminder(
                user.apiKey,
                payload,
                user.authToken,
                user.identityToken
            );
            const linearReminders = response?.data?.reminders ?? [];
            return linearReminders;
        },
    });

export const setReminderMutationFn = async (payload: SetReminderPropsTypes & { user: UserInfo; language: string }) => {
    const reminderService = container.resolve<IReminderService>(DI.SERVICES.IReminderService);
    return await reminderService.setReminder(
        payload.user.apiKey,
        {
            paId: Number(excludeIdFromUrl(payload.paId)),
            listingId: Number(excludeIdFromUrl(payload.listingId)),
        },
        payload.user.authToken,
        payload.user.identityToken,
        payload.language,
        payload?.type
    );
};

export const unsetReminderMutationFn = async (payload: UnSetReminderPropsTypes & { user: UserInfo }) => {
    const reminderService = container.resolve<IReminderService>(DI.SERVICES.IReminderService);
    return await reminderService.unSetReminder(
        payload.user.apiKey,
        {
            reminderIds: [payload.reminderId.toString()],
        },
        payload.user.authToken,
        payload.user.identityToken
    );
};

export const setReminder = createAsyncThunk('set/reminder', async (payload: SetReminderPropsTypes, { getState }) => {
    const reminderService = container.resolve<IReminderService>(DI.SERVICES.IReminderService);
    const response: any = await reminderService.setReminder(
        user.getAPIKey(),
        {
            paId: Number(excludeIdFromUrl(payload.paId)),
            listingId: Number(excludeIdFromUrl(payload.listingId)),
        },
        user.getUserToken(),
        user.getIdentityToken(),
        h.getLang(),
        payload?.type
    );
    if (response && response?.data) {
        // Add the listingId to the custom attribute array
        braze?.getUser()?.addToCustomAttributeArray('reminder_programs_listing_ids', payload.listingId);
        braze?.getUser()?.setLanguage(h.getLang());

        // Correctly type the state
        const state = getState() as RootState;

        // Get the current reminders from the state
        const currentReminders = (state.reminder?.remindersData || []) as SetReminderPropsTypes[];

        // Add the new reminder to the current array of reminders
        const updatedReminders = [...currentReminders, response.data];
        // Return the updated array of reminders
        return updatedReminders;
    }
});

export const unsetReminder = createAsyncThunk(
    'unset/reminder',
    async (payload: UnSetReminderPropsTypes, { getState }) => {
        const reminderService = container.resolve<IReminderService>(DI.SERVICES.IReminderService);
        const response = await reminderService.unSetReminder(
            user.getAPIKey(),
            {
                reminderIds: [payload.reminderId.toString()],
            },
            user.getUserToken(),
            user.getIdentityToken()
        );

        if (response && response.data) {
            // Remove the reminder from Braze
            braze?.getUser()?.removeFromCustomAttributeArray('reminder_programs_listing_ids', payload?.listingId);

            // Get the current state
            const state = getState() as RootState;

            // Get the current reminders from the state, fallback to an empty array
            const currentReminders = (state.reminder?.remindersData || []) as SetReminderPropsTypes[];

            // Filter out the reminder that matches the reminderId
            const updatedReminders = currentReminders.filter((reminder) => reminder.reminderId !== payload.reminderId);

            return updatedReminders;
        }
    }
);

const reminderSlice = createSlice({
    name: 'Reminder',
    initialState: initialState.reminder,
    reducers: {
        showReminderPanel: (state: IReminderReducer, action) => {
            state.showPanel = action.payload;
        },
        setCurrentPanelItem: (state: IReminderReducer, action) => {
            state.item = action.payload;
            state.currentIndex = action.payload.index;
        },
        reminderSetState: (state: IReminderReducer) => {
            state.state = '';
        },
    },
    extraReducers: (builder: any) => {
        // set reminder cases
        builder.addCase(
            setReminder.pending,
            (state: IReminderReducer, action: { meta: { arg: { index: number } } }) => {
                state.isLoading = true;
                state.currentIndex = action.meta.arg.index;
            }
        );
        builder.addCase(setReminder.fulfilled, (state: IReminderReducer, action: { payload: ReminderData[] }) => {
            state.state = 'set';
            state.remindersData = action.payload;
            state.isLoading = false;
        });
        builder.addCase(setReminder.rejected, (state: IReminderReducer, action: { error: { message: string } }) => {
            state.state = 'errorSet';
            state.errMess = action.error.message;
            state.isLoading = false;
        });

        // unset reminder cases
        builder.addCase(
            unsetReminder.pending,
            (state: IReminderReducer, action: { meta: { arg: { index: number } } }) => {
                state.isLoading = true;
                state.currentIndex = action.meta.arg.index;
            }
        );
        builder.addCase(unsetReminder.fulfilled, (state: IReminderReducer, action: { payload: ReminderData[] }) => {
            state.state = 'unset';
            state.remindersData = action.payload;
            state.isLoading = false;
        });
        builder.addCase(
            unsetReminder.rejected,
            (state: IReminderReducer, action: { error: { message: ReminderData[] } }) => {
                state.state = 'errorUnset';
                state.remindersData = action.error.message;
                state.isLoading = false;
            }
        );
    },
});

export const { showReminderPanel, setCurrentPanelItem, reminderSetState } = reminderSlice.actions;

export default reminderSlice.reducer;
